import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { convertProsemirrorToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';

import { addAnalytics } from '../analytics/utils';
import { ErrorUtils } from '../utils/errors';
import { type MentionMap } from '../utils/mentions/get-mention-map';

import type { GetInitialContext } from './config-items';
import { getContextSelectionScope } from './utils';

type UpdateIdMap = Parameters<GetInitialContext>[0]['updateIdMap'];

type SliceToMarkdownProps = {
	editorView: EditorView;
	/**
	 * @default markdown
	 */
	convertTo: 'markdown-plus' | 'markdown';
	updateIdMap: UpdateIdMap;
	node?: undefined;
	slice: Slice;
	selectionType: 'range' | 'empty';
	mentionMap?: MentionMap;
};
type NodeToMarkdownProps = {
	editorView: EditorView;
	/**
	 * @default markdown
	 */
	convertTo: 'markdown-plus' | 'markdown';
	updateIdMap: UpdateIdMap;
	node: PMNode;
	slice?: undefined;
	selectionType: 'range' | 'empty';
	mentionMap?: MentionMap;
};
type SliceOrNodeToMarkdownProps = SliceToMarkdownProps | NodeToMarkdownProps;

/**
 * Takes a prosemirror schema and (slice or node) and returns it as
 * markdown / markdown plus.
 * ```
 */
export function sliceOrNodeToMarkdown({
	slice,
	node,
	editorView,
	convertTo,
	updateIdMap,
	selectionType,
	mentionMap,
}: SliceOrNodeToMarkdownProps): {
	markdown: string;
	contentStatistics?: ContentStatistics;
} {
	let fragment = (slice || node).content;
	try {
		// we track whether the context is a whole document or a partial selection
		const contextSelectionScope = getContextSelectionScope({
			editorView,
			slice,
			node,
		});
		if (convertTo === 'markdown-plus') {
			const { markdown, idMap, fallbackState, contentStatistics } = convertProsemirrorToMarkdown({
				editorView,
				fragment,
				featureToggles: {
					markdownPlus: true,
					markdownPlusExtensions: true,
					markdownPlusPanels: true,
					markdownPlusDecisions: true,
				},
				mentionMap: mentionMap,
			});

			if (!!Object.keys(fallbackState).length) {
				const tr = addAnalytics({
					editorState: editorView.state,
					tr: editorView.state.tr,
					payload: {
						action: 'fallback',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'markdownSerializer',
						attributes: {
							fallbackState,
						},
						eventType: EVENT_TYPE.OPERATIONAL,
					},
				});

				tr.setMeta('addToHistory', false);

				editorView.dispatch(tr);
			}

			// This ID map is used to map the markdown back to the original ProseMirror
			// when we receive transformed content back from the llm.
			updateIdMap({ idMap, selectionType });

			return {
				markdown,
				contentStatistics: { ...contentStatistics, contextSelectionScope },
			};
		}

		const { markdown, contentStatistics } = convertProsemirrorToMarkdown({
			editorView,
			fragment,
			featureToggles: {},
			mentionMap: mentionMap,
		});

		return {
			markdown,
			contentStatistics: { ...contentStatistics, contextSelectionScope },
		};
	} catch (e) {
		const tr = addAnalytics({
			editorState: editorView.state,
			tr: editorView.state.tr,
			payload: {
				action: 'failed',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'markdownSerializer',
				attributes: {
					errorType: 'markdownSerializer',
					errorMessage: ErrorUtils.extractErrorMessage(e),
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			},
		});

		editorView.dispatch(tr);

		return { markdown: '', contentStatistics: undefined };
	}
}
